import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Button from "components/Buttons/Button";
import Calendar from "components/Calendar/Calendar";
import SegmentedControl from "components/SegmentedControl/SegmentedControl";
import useOnClickOutside from "hooks/useOnClickOutside";
import { pxToRem } from "styles/utils";

export type PeriodPreset = "30" | "90" | "365" | "all" | "custom";

export interface DateRangeValue {
  from: Date;
  to: Date;
}

interface PeriodSelectorProps {
  value: PeriodPreset;
  range: DateRangeValue;
  minDate?: Date;
  maxDate?: Date;
  onPresetChange: (value: Exclude<PeriodPreset, "custom">) => void;
  onCustomApply: (range: DateRangeValue) => void;
  className?: string;
}

const Container = styled.div`
  position: relative;
  display: inline-flex;
  min-width: 0;
`;

const PickerPanel = styled.div`
  position: absolute;
  top: calc(100% + ${pxToRem(10)});
  left: 0;
  z-index: 40;
  width: max-content;
  max-width: min(${pxToRem(540)}, calc(100vw - ${pxToRem(48)}));
  padding: ${pxToRem(20)};
  border: ${pxToRem(1.25)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${pxToRem(15)};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0 ${pxToRem(10)} ${pxToRem(35)} rgba(31, 10, 53, 0.12);
`;

const RangeSummary = styled.div`
  display: flex;
  gap: ${pxToRem(20)};
  margin-bottom: ${pxToRem(12)};
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${pxToRem(15)};
`;

const RangeValue = styled.span`
  color: ${({ theme }) => theme.colors.ink};
  font-weight: 500;
`;

const ErrorText = styled.div`
  margin-top: ${pxToRem(10)};
  color: #b0304c;
  font-size: ${pxToRem(15)};
`;

const PickerActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${pxToRem(10)};
  margin-top: ${pxToRem(14)};

  > button {
    width: auto;
    min-width: ${pxToRem(100)};
    max-width: none;
  }
`;

const OPTIONS = [
  { label: "30 дней", value: "30" },
  { label: "3 месяца", value: "90" },
  { label: "12 месяцев", value: "365" },
  { label: "Всё время", value: "all" },
  { label: "Период", value: "custom" },
];

const formatDate = (value: Date | null) =>
  value ? value.toLocaleDateString("ru-RU") : "—";

const clampDateToBounds = (
  value: Date,
  minDate?: Date,
  maxDate?: Date,
): Date => {
  if (minDate && value.getTime() < minDate.getTime()) {
    return minDate;
  }
  if (maxDate && value.getTime() > maxDate.getTime()) {
    return maxDate;
  }
  return value;
};

const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  value,
  range,
  minDate,
  maxDate,
  onPresetChange,
  onCustomApply,
  className,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState<Date | null>(range.from);
  const [draftTo, setDraftTo] = useState<Date | null>(range.to);
  const [error, setError] = useState<string | null>(null);

  useOnClickOutside(rootRef, () => setIsOpen(false));

  useEffect(() => {
    if (isOpen) {
      const boundedFrom = clampDateToBounds(range.from, minDate, maxDate);
      const boundedTo = clampDateToBounds(range.to, minDate, maxDate);
      setDraftFrom(boundedFrom);
      setDraftTo(
        boundedTo.getTime() < boundedFrom.getTime() ? boundedFrom : boundedTo,
      );
      setError(null);
    }
  }, [isOpen, range.from, range.to, minDate, maxDate]);

  const handleSegmentChange = (nextValue: string) => {
    const preset = nextValue as PeriodPreset;
    if (preset === "custom") {
      setIsOpen(true);
      return;
    }
    setIsOpen(false);
    onPresetChange(preset);
  };

  const handleApply = () => {
    if (!draftFrom || !draftTo) {
      setError("Выберите начало и конец периода");
      return;
    }
    if (draftFrom.getTime() > draftTo.getTime()) {
      setError("Начало периода позже конца");
      return;
    }
    onCustomApply({ from: draftFrom, to: draftTo });
    setIsOpen(false);
    setError(null);
  };

  return (
    <Container ref={rootRef} className={className}>
      <SegmentedControl
        value={value}
        options={OPTIONS}
        onChange={handleSegmentChange}
        ariaLabel="Период статистики"
      />

      {isOpen && (
        <PickerPanel role="dialog" aria-label="Произвольный период">
          <RangeSummary>
            <span>С <RangeValue>{formatDate(draftFrom)}</RangeValue></span>
            <span>По <RangeValue>{formatDate(draftTo)}</RangeValue></span>
          </RangeSummary>
          <Calendar
            startDate={draftFrom ?? undefined}
            endDate={draftTo ?? undefined}
            onDateChange={([from, to]) => {
              setDraftFrom(from);
              setDraftTo(to);
              setError(null);
            }}
            isRange
            minDate={minDate}
            maxDate={maxDate}
          />
          {error && <ErrorText role="alert">{error}</ErrorText>}
          <PickerActions>
            <Button variant="secondary" size="sm" onClick={() => setIsOpen(false)}>
              Отмена
            </Button>
            <Button variant="primary" size="sm" onClick={handleApply}>
              Применить
            </Button>
          </PickerActions>
        </PickerPanel>
      )}
    </Container>
  );
};

export default PeriodSelector;
